import { ChangeEventHandler, Fragment, useEffect, useState } from "react";
import { Popover, Transition } from "@headlessui/react";
import { ChatAlt2Icon, SearchIcon } from "@heroicons/react/outline";
import { useTranslation } from "react-i18next";
import ShowIf from "../show-if/ShowIf";
import { AuthContextInterface, useAuth } from "../../context/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import { ChatContact } from "../../api/models/ConversationModel";
import { Message } from "@twilio/conversations";
import { useEffectOnce } from "../../hooks/useEffectOnce";
import { useChat } from "../../context/ChatContext";
import ChatContactsList from "./ChatContactsList";
import LoadingSpinner from "../LoadingSpinner";
import { GetCurrentWindowSize } from "../../hooks/useWindowSize";

export default function ChatMenuNavbar() {
  const { t } = useTranslation("", {
    keyPrefix: "page.chat",
  });
  const { pathname: currentPath } = useLocation();
  const { currentUser }: AuthContextInterface = useAuth()!;
  const [showSearchResults, setShowSearchResults] = useState<boolean>(false);
  const [hasUnreadMessages, setHasUnreadMessages] = useState<boolean>(false);
  const navigate = useNavigate();
  const currentWindow = GetCurrentWindowSize();

  const viewConversations = (
    chatContact: ChatContact | undefined = undefined
  ) => {
    const userType = currentUser!.object_type!.toLowerCase();
    let pathUrl = `/${userType}/dashboard/messages`;
    if (!!chatContact) {
      pathUrl = `${pathUrl}?conversation_id=${chatContact.conversationTwillioId!}`;
    }
    if (pathUrl !== currentPath) {
      navigate(pathUrl);
    }
  };

  const onMobileClick = () => {
    if (currentWindow === "sm") {
      viewConversations();
    }
  };

  const {
    isInitialized,
    initializeChat,
    refreshConversations,
    conversationContacts,
    search,
    resetSearchResults,
    filteredConversationContacts,
    unreadMessagesCounter,
  } = useChat();

  useEffectOnce(
    () => initializeChat(handleChatInitialized, onMessageAddedHandler),
    true
  );

  useEffect(() => {
    setHasUnreadMessages(unreadMessagesCounter > 0);
  }, [unreadMessagesCounter]);

  const handleChatInitialized = () => {
    refreshConversations();
  };

  const onMessageAddedHandler = (message: Message) => {
    setHasUnreadMessages(true);
    refreshConversations();
  };

  const onSearch: ChangeEventHandler<HTMLInputElement> = (e) => {
    const val: string = e.target.value;
    if (val === "") {
      resetSearchResults();
      setShowSearchResults(false);
    } else {
      search(val);
      setShowSearchResults(true);
    }
  };

  if (!isInitialized) {
    return <LoadingSpinner size={20} />;
  }

  return (
    <Popover className="relative z-[9999] ">
      {({ open }) => (
        <>
          <Popover.Button className="inline-flex items-center focus:outline-none relative">
            <ChatAlt2Icon
              onClick={onMobileClick}
              className="w-6 h-6 flex text-gray-400"
            />
            <ShowIf condition={hasUnreadMessages}>
              <div
                key={"unreadMessagesCounter"}
                className="absolute inline-flex items-center justify-center w-2.5 h-2.5 text-xs font-bold text-white bg-red-600   rounded-full -top-0.5 -right-0"
              />
            </ShowIf>
          </Popover.Button>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
            show={open}
          >
            <Popover.Panel className="absolute  hidden md:block max-h-[544px] rtl:right-1/2 ltr:left-1/2 z-10 mt-9 w-screen max-w-md px-2 sm:px-0">
              {({ close }) => (
                <>
                  <div className="overflow-hidden flex bg-white flex-col justify-between md:justify-start  h-full md:rounded-md md:shadow-lg">
                    <div className="relative rounded-md shadow-sm mx-4 my-2">
                      <div className="absolute inset-y-0 start-0 ps-3  flex items-center pointer-events-none">
                        <SearchIcon
                          className="h-5 w-5 text-gray-400"
                          aria-hidden="true"
                        />
                      </div>
                      <input
                        type="text"
                        name="search"
                        id="search"
                        className="block w-full ps-10 border border-gray-300 focus:ring-brandblue-500 focus:border-brandblue-500 text-sm rounded-md"
                        placeholder={t("searchByName")}
                        onChange={onSearch}
                      />
                    </div>
                    <div className="max-h-[50vh] overflow-y-auto">
                      <ChatContactsList
                        contacts={
                          showSearchResults
                            ? filteredConversationContacts
                            : conversationContacts
                        }
                        isLoading={false}
                        onSelectConversation={viewConversations}
                      />
                    </div>
                    <div className="sticky bg-white  mt-auto z-[999] w-full  bottom-0  flex justify-center items-center  px-4 pt-4 pb-6 md:px-6">
                      <button
                        type="button"
                        onClick={() => {
                          viewConversations();
                          close();
                        }}
                        className="text-gray-700  flex justify-center items-center h-9 w-full py-2 text-sm font-medium bg-white border md:border-none border-gray-300 rounded-md shadow md:shadow-none"
                      >
                        {t("moreMessages")}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
}
